import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    EntityActionFactory,
    EntityCacheDispatcher, EntityOp,
    HttpUrlGenerator, Logger, MergeStrategy, QueryParams
} from '@ngrx/data';
import { Observable, Subscription } from 'rxjs/index';
import { map } from 'rxjs/operators';
import { WebsocketBrokerService } from '../../core/services/websocket-broker.service';
import { WebSocketEntityDataServiceBase } from '../../state/entity/web-socket-entity-data-service-base';
import { Message } from '../message.model';
import { patientMessageEntityMetadata } from './message.entity';

export const entityActionFactory = new EntityActionFactory();

/**
 * This is the base data service to get messages (called by NgRx Effects to make actuall calls to the backend).
 * This must be specialized e.g. to get PersonMessage entity data.
 */
export abstract class MessageEntityDataServiceBase<T> extends WebSocketEntityDataServiceBase<Message> {
    subscriptions: Subscription = new Subscription();

    constructor(http: HttpClient,
                httpUrlGenerator: HttpUrlGenerator,
                logger: Logger,
                public entityCacheDispatcher: EntityCacheDispatcher,
                public websocketBrokerService: WebsocketBrokerService,
                entityName: string,
                public webSocketTopic: string) {
        super(entityName, http, httpUrlGenerator);
    }

    getWithQuery(parameters: string | QueryParams): Observable<Array<Message>> {
        return super.getWithQuery(parameters) || this.getMessages(parameters);
    }

    abstract getMessages(parameters: string | QueryParams): Observable<Array<Message>>;

    subscribeWebSocketJobWithBody(initialBody: any): void {
       if (window.self === window.top) {
          // NOTE: Since we're not subscribing to the result inside an NgRx Effect we need to subscribe here and manage the
          //       subscription.
          const websocketBrokerServiceSubscription =
             this.websocketBrokerService.getRemoteObservable(this.webSocketTopic, initialBody)
                .pipe(
                   map(pushedPayload => {
                      const data: any = [];
                      // For some reason the array from the backend is not useful for adding properties
                      // so we create a new adding the messages
                      pushedPayload.messages.forEach(message => {
                         data.push(message);
                      });

                      data.queryFilter = pushedPayload.queryFilter; // Must be added as property to the array object
                      const entityAction = entityActionFactory.create<Array<Message>>(
                         patientMessageEntityMetadata.entityName,
                         EntityOp.QUERY_MANY_SUCCESS,
                         data,
                         {
                            tag: 'MessageEffects',
                            mergeStrategy: MergeStrategy.OverwriteChanges
                         }
                      );
                      this.entityCacheDispatcher.dispatch(entityAction);
                   })
                )
                .subscribe();
          this.subscriptions.add(websocketBrokerServiceSubscription);
       }
    }

    unsubscribeWebSocketJob(): void {
        this.websocketBrokerService.unsubscribe(this.webSocketTopic);
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
    }

    refreshWebSocketJobWithBody(messageBody: any): void {
        this.websocketBrokerService.sendAction('refresh', this.webSocketTopic, messageBody);
    }
}
